import { useState } from "react";
import axios from "axios";

/**
 * BlogWorkbookCTA — gold in-article email-capture box for the free workbook.
 * Gold #F4C36A bg, Deep Teal #1B6B6E text, deep-teal button w/ cream #F4F1EC.
 * Submits → MailerLite group 192377413184783762 (same flow as homepage hero),
 * fires Meta Pixel Lead + a custom event distinguishing Box A vs Box B,
 * then redirects to /workbook-success.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;
const WORKBOOK_GROUP_ID = "192377413184783762";

const COPY = {
  A: {
    headline: "If this story is your story —",
    body:
      "You don't have to hold it alone, friend. The free I GET TO Workbook — 27 pages of real tools for the messy middle — is where I'd start. No retreat required. Just your email, and it's yours.",
    button: "Send Me the Free Workbook",
    gaEvent: "blog_workbook_box_a",
    fbEvent: "BlogWorkbookLeadA",
  },
  B: {
    headline: null,
    body:
      "This is the part where I stopped saying I have to and started living I GET TO. The free workbook walks you through that exact reframe — one page, one exhale at a time.",
    button: "I GET TO Start Here",
    gaEvent: "blog_workbook_box_b",
    fbEvent: "BlogWorkbookLeadB",
  },
};

export const BlogWorkbookCTA = ({ variant = "A", slug }) => {
  const c = COPY[variant] || COPY.A;
  const v = variant.toLowerCase();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    setErr("");
    try {
      await axios.post(`${API_URL}/api/subscribe`, {
        email: email.trim().toLowerCase(),
        firstName: "Friend",
        group_id: WORKBOOK_GROUP_ID,
        source: `blog-${slug || "post"}-workbook-box-${v}`,
      });
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", c.gaEvent, { source_page: "blog", slug: slug || "", box: variant });
        window.gtag("event", "generate_lead", { currency: "USD", value: 0 });
      }
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "Free I GET TO Workbook", content_category: "blog-cta", value: 0, currency: "USD" });
        window.fbq("trackCustom", c.fbEvent, { slug: slug || "" });
      }
      try { localStorage.setItem("mbsh_subscribed", "true"); } catch (_) { /* ignore */ }
      window.location.href = "/workbook-success";
    } catch (error) {
      const status = error?.response?.status;
      if (status === 400 || status === 409) {
        window.location.href = "/workbook-success";
        return;
      }
      setErr("Something hiccupped. Please try again in a moment.");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="my-10 rounded-2xl p-7 md:p-8"
      style={{ background: "#F4C36A", color: "#1B6B6E" }}
      data-testid={`blog-workbook-cta-${v}`}
    >
      {c.headline && (
        <h3
          className="font-serif text-2xl md:text-3xl mb-3 leading-tight"
          style={{ color: "#1B6B6E" }}
          data-testid={`blog-workbook-headline-${v}`}
        >
          {c.headline}
        </h3>
      )}
      <p className="font-sans text-base md:text-lg leading-relaxed mb-6" style={{ color: "#1B6B6E" }}>
        {c.body}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your email"
          aria-label="Email address"
          className="flex-1 px-5 py-3.5 rounded-full font-sans text-sm outline-none border-0"
          style={{ background: "#F4F1EC", color: "#1B6B6E" }}
          data-testid={`blog-workbook-email-${v}`}
        />
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-sans font-bold tracking-wide transition-transform hover:-translate-y-0.5 disabled:opacity-60 whitespace-nowrap"
          style={{ background: "#1B6B6E", color: "#F4F1EC", fontSize: "15px" }}
          data-testid={`blog-workbook-submit-${v}`}
        >
          {submitting ? "Sending…" : c.button}
        </button>
      </form>
      {err && (
        <p className="font-sans text-xs mt-3" style={{ color: "#7f1d1d" }} data-testid={`blog-workbook-error-${v}`}>
          {err}
        </p>
      )}
      <p className="font-sans text-xs mt-3" style={{ color: "#1B6B6E", opacity: 0.75 }}>
        Free. No spam. Unsubscribe anytime.
      </p>
    </div>
  );
};

export default BlogWorkbookCTA;
